import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Plus, Trash2, GripVertical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40 focus:border-prepify-500 transition"
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40 focus:border-prepify-500 transition"
  />
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex items-center gap-2 text-sm font-medium transition ${checked ? "text-prepify-600" : "text-slate-400"}`}
    data-testid="section-toggle"
  >
    {checked ? <Eye size={16} /> : <EyeOff size={16} />}
    {label || (checked ? "Visible" : "Hidden")}
  </button>
);

const SectionShell = ({ title, desc, isVisible, onToggle, children, accent = "prepify" }) => (
  <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
    <header className="flex items-start justify-between mb-5 pb-4 border-b border-slate-100">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        {desc && <p className="text-sm text-slate-500 mt-1">{desc}</p>}
      </div>
      {onToggle && <Toggle checked={isVisible} onChange={onToggle} />}
    </header>
    <div className="space-y-4">{children}</div>
  </section>
);

const CardList = ({ items, onChange, renderItem, emptyLabel, onAdd, addLabel }) => {
  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next.map((it, i) => ({ ...it, order: i })));
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="text-sm text-slate-400 italic text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">{emptyLabel}</p>
      )}
      {items.map((it, idx) => (
        <div key={it.id || idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
          <div className="flex items-center gap-2 mb-3">
            <GripVertical size={16} className="text-slate-300" />
            <div className="flex-1 flex items-center gap-2">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="px-2 py-0.5 text-xs border border-slate-200 rounded disabled:opacity-30 hover:bg-white">↑</button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="px-2 py-0.5 text-xs border border-slate-200 rounded disabled:opacity-30 hover:bg-white">↓</button>
              <Toggle checked={it.is_visible !== false} onChange={(v) => update(idx, { is_visible: v })} />
            </div>
            <button type="button" onClick={() => remove(idx)} className="text-red-500 hover:bg-red-50 p-1.5 rounded">
              <Trash2 size={14} />
            </button>
          </div>
          {renderItem(it, idx, update)}
        </div>
      ))}
      <button type="button" onClick={onAdd} className="w-full border-2 border-dashed border-prepify-300 text-prepify-700 hover:bg-prepify-50 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2">
        <Plus size={16} /> {addLabel}
      </button>
    </div>
  );
};

const SimpleListEditor = ({ items, onChange, placeholder }) => {
  const update = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} />
          <button type="button" onClick={() => remove(i)} className="text-red-500 hover:bg-red-50 px-2 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ""])} className="text-xs text-prepify-700 hover:underline">
        + Add item
      </button>
    </div>
  );
};

const HomepageEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cms/homepage`).then((res) => setData(res.data)).catch((e) => toast.error("Failed to load content"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/homepage`, data);
      toast.success("Homepage content saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const patch = (section, update) => setData({ ...data, [section]: { ...data[section], ...update } });

  if (!data) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      <header className="flex items-center justify-between sticky top-0 bg-[#F8FAFC]/95 backdrop-blur py-3 z-10 -mx-6 px-6 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Homepage Content</h1>
          <p className="text-sm text-slate-500">Edit every section of the public homepage. Changes go live immediately.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50 transition-colors" data-testid="save-homepage-btn">
          <Save size={16} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </header>

      {/* HERO */}
      <SectionShell
        title="Hero Section"
        desc="The first thing every visitor sees — big headline, image, and primary CTAs."
        isVisible={data.hero.is_visible}
        onToggle={(v) => patch("hero", { is_visible: v })}
      >
        <Field label="Badge Text"><Input value={data.hero.badge_text} onChange={(e) => patch("hero", { badge_text: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Headline Line 1"><Input value={data.hero.headline_line_1} onChange={(e) => patch("hero", { headline_line_1: e.target.value })} /></Field>
          <Field label="Headline Line 2"><Input value={data.hero.headline_line_2} onChange={(e) => patch("hero", { headline_line_2: e.target.value })} /></Field>
        </div>
        <Field label="Highlighted Word" hint="This word in Line 2 will appear in green">
          <Input value={data.hero.highlight_word} onChange={(e) => patch("hero", { highlight_word: e.target.value })} />
        </Field>
        <Field label="Subtitle"><TextArea rows={3} value={data.hero.subtitle} onChange={(e) => patch("hero", { subtitle: e.target.value })} /></Field>
        <Field label="Hero Image URL" hint="Displayed as circular portrait inline with headline">
          <Input value={data.hero.hero_image_url} onChange={(e) => patch("hero", { hero_image_url: e.target.value })} placeholder="https://… or /api/public/media/{id}" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-600 mb-2">PRIMARY CTA</p>
            <Input placeholder="Label" value={data.hero.primary_cta?.label || ""} onChange={(e) => patch("hero", { primary_cta: { ...data.hero.primary_cta, label: e.target.value } })} />
            <Input placeholder="#contact or /path" className="mt-2" value={data.hero.primary_cta?.href || ""} onChange={(e) => patch("hero", { primary_cta: { ...data.hero.primary_cta, href: e.target.value } })} />
          </div>
          <div className="border border-slate-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-600 mb-2">SECONDARY CTA</p>
            <Input placeholder="Label" value={data.hero.secondary_cta?.label || ""} onChange={(e) => patch("hero", { secondary_cta: { ...data.hero.secondary_cta, label: e.target.value } })} />
            <Input placeholder="#services" className="mt-2" value={data.hero.secondary_cta?.href || ""} onChange={(e) => patch("hero", { secondary_cta: { ...data.hero.secondary_cta, href: e.target.value } })} />
          </div>
        </div>
        <Field label="Stats (green pill bar)" hint="Click + to add. Each stat needs a number and a label.">
          <div className="space-y-2">
            {data.hero.stats.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="50+" value={s.num} onChange={(e) => { const next = [...data.hero.stats]; next[i] = { ...s, num: e.target.value }; patch("hero", { stats: next }); }} />
                <Input placeholder="Institutes" value={s.label} onChange={(e) => { const next = [...data.hero.stats]; next[i] = { ...s, label: e.target.value }; patch("hero", { stats: next }); }} />
                <button type="button" onClick={() => patch("hero", { stats: data.hero.stats.filter((_, j) => j !== i) })} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => patch("hero", { stats: [...data.hero.stats, { num: "", label: "" }] })} className="text-xs text-prepify-700 hover:underline">+ Add stat</button>
          </div>
        </Field>
      </SectionShell>

      {/* MARQUEE */}
      <SectionShell title="Marquee Banner" desc="Scrolling ticker below the hero with asterisk separators." isVisible={data.marquee.is_visible} onToggle={(v) => patch("marquee", { is_visible: v })}>
        <Field label="Items" hint="Each word scrolls past with a spinning ✳ between them">
          <SimpleListEditor items={data.marquee.items} onChange={(v) => patch("marquee", { items: v })} placeholder="Institute name or offering" />
        </Field>
      </SectionShell>

      {/* SERVICES */}
      <SectionShell title="Services Grid" desc="The 6-card grid showing what you offer." isVisible={data.services.is_visible} onToggle={(v) => patch("services", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.services.eyebrow} onChange={(e) => patch("services", { eyebrow: e.target.value })} /></Field>
        <Field label="Title" hint="HTML allowed. Wrap highlighted word with <em>…</em>. Use <br/> for line breaks.">
          <TextArea rows={2} value={data.services.title} onChange={(e) => patch("services", { title: e.target.value })} />
        </Field>
        <Field label="Description"><TextArea rows={2} value={data.services.description} onChange={(e) => patch("services", { description: e.target.value })} /></Field>
        <Field label="Cards">
          <CardList
            items={data.services.cards}
            emptyLabel="No service cards yet"
            addLabel="Add service card"
            onAdd={() => patch("services", { cards: [...data.services.cards, { id: crypto.randomUUID(), icon: "FileText", title: "New Service", description: "", pills: [], order: data.services.cards.length, is_visible: true }] })}
            onChange={(cards) => patch("services", { cards })}
            renderItem={(c, idx, up) => (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Icon" hint="FileText, Newspaper, Calendar, Scale, BookOpen, Target, Shield, Users, Rocket, Zap, Award, Sparkles, Brain">
                  <Input value={c.icon} onChange={(e) => up(idx, { icon: e.target.value })} />
                </Field>
                <Field label="Title"><Input value={c.title} onChange={(e) => up(idx, { title: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Description"><TextArea rows={2} value={c.description} onChange={(e) => up(idx, { description: e.target.value })} /></Field></div>
                <div className="col-span-2"><Field label="Pills (comma-separated)"><Input value={(c.pills || []).join(", ")} onChange={(e) => up(idx, { pills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field></div>
              </div>
            )}
          />
        </Field>
      </SectionShell>

      {/* PROCESS */}
      <SectionShell title="How It Works" desc="4-step process with overlapping numbered cards." isVisible={data.process.is_visible} onToggle={(v) => patch("process", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.process.eyebrow} onChange={(e) => patch("process", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.process.title} onChange={(e) => patch("process", { title: e.target.value })} /></Field>
        <Field label="Steps">
          <CardList
            items={data.process.steps}
            emptyLabel="No steps yet"
            addLabel="Add step"
            onAdd={() => patch("process", { steps: [...data.process.steps, { id: crypto.randomUUID(), num: String(data.process.steps.length + 1).padStart(2, "0"), title: "New Step", description: "", order: data.process.steps.length, is_visible: true }] })}
            onChange={(steps) => patch("process", { steps })}
            renderItem={(s, idx, up) => (
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-1"><Field label="Number"><Input value={s.num} onChange={(e) => up(idx, { num: e.target.value })} /></Field></div>
                <div className="col-span-5"><Field label="Title"><Input value={s.title} onChange={(e) => up(idx, { title: e.target.value })} /></Field></div>
                <div className="col-span-6"><Field label="Description"><TextArea rows={2} value={s.description} onChange={(e) => up(idx, { description: e.target.value })} /></Field></div>
              </div>
            )}
          />
        </Field>
      </SectionShell>

      {/* JOURNEY */}
      <SectionShell title="Our Journey (Timeline)" desc="Vertical timeline of company milestones." isVisible={data.journey.is_visible} onToggle={(v) => patch("journey", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.journey.eyebrow} onChange={(e) => patch("journey", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.journey.title} onChange={(e) => patch("journey", { title: e.target.value })} /></Field>
        <Field label="Milestones">
          <CardList
            items={data.journey.milestones}
            emptyLabel="No milestones yet"
            addLabel="Add milestone"
            onAdd={() => patch("journey", { milestones: [...data.journey.milestones, { id: crypto.randomUUID(), year: "", title: "", description: "", icon: "Rocket", order: data.journey.milestones.length, is_visible: true }] })}
            onChange={(milestones) => patch("journey", { milestones })}
            renderItem={(m, idx, up) => (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Year"><Input value={m.year} onChange={(e) => up(idx, { year: e.target.value })} /></Field>
                <Field label="Title"><Input value={m.title} onChange={(e) => up(idx, { title: e.target.value })} /></Field>
                <Field label="Icon" hint="Rocket, Target, Brain, Zap, Award"><Input value={m.icon} onChange={(e) => up(idx, { icon: e.target.value })} /></Field>
                <div className="col-span-3"><Field label="Description"><TextArea rows={2} value={m.description} onChange={(e) => up(idx, { description: e.target.value })} /></Field></div>
              </div>
            )}
          />
        </Field>
      </SectionShell>

      {/* PARTNER LOGOS SECTION */}
      <SectionShell title="Partner Logos Section" desc="The headline/eyebrow shown above the logos grid. Manage actual logos in Partner Logos." isVisible={data.partner_logos_section.is_visible} onToggle={(v) => patch("partner_logos_section", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.partner_logos_section.eyebrow} onChange={(e) => patch("partner_logos_section", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.partner_logos_section.title} onChange={(e) => patch("partner_logos_section", { title: e.target.value })} /></Field>
        <Field label="Description"><TextArea rows={2} value={data.partner_logos_section.description} onChange={(e) => patch("partner_logos_section", { description: e.target.value })} /></Field>
      </SectionShell>

      {/* ABOUT / FOUNDER */}
      <SectionShell title="About / Founder" desc="Editorial block with founder portrait and quote." isVisible={data.about.is_visible} onToggle={(v) => patch("about", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.about.eyebrow} onChange={(e) => patch("about", { eyebrow: e.target.value })} /></Field>
        <Field label="Quote (HTML allowed)"><TextArea rows={4} value={data.about.quote} onChange={(e) => patch("about", { quote: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Founder Name"><Input value={data.about.founder_name} onChange={(e) => patch("about", { founder_name: e.target.value })} /></Field>
          <Field label="Founder Role"><Input value={data.about.founder_role} onChange={(e) => patch("about", { founder_role: e.target.value })} /></Field>
        </div>
        <Field label="Founder Image URL"><Input value={data.about.founder_image_url} onChange={(e) => patch("about", { founder_image_url: e.target.value })} /></Field>
        <Field label="KPIs">
          <div className="space-y-2">
            {data.about.kpis.map((k, i) => (
              <div key={k._uid || `kpi-${i}`} className="flex gap-2">
                <Input placeholder="2021" value={k.num} onChange={(e) => { const n = [...data.about.kpis]; n[i] = { ...k, num: e.target.value }; patch("about", { kpis: n }); }} />
                <Input placeholder="Year Founded" value={k.label} onChange={(e) => { const n = [...data.about.kpis]; n[i] = { ...k, label: e.target.value }; patch("about", { kpis: n }); }} />
                <button type="button" onClick={() => patch("about", { kpis: data.about.kpis.filter((_, j) => j !== i) })} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => patch("about", { kpis: [...data.about.kpis, { _uid: crypto.randomUUID(), num: "", label: "" }] })} className="text-xs text-prepify-700 hover:underline">+ Add KPI</button>
          </div>
        </Field>
        <Field label="Pillars (Mission / Vision / Values)">
          <div className="space-y-3">
            {data.about.pillars.map((p, i) => (
              <div key={p._uid || `pillar-${i}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Title (Mission)" value={p.title} onChange={(e) => { const n = [...data.about.pillars]; n[i] = { ...p, title: e.target.value }; patch("about", { pillars: n }); }} />
                  <Input placeholder="Icon (Target)" value={p.icon} onChange={(e) => { const n = [...data.about.pillars]; n[i] = { ...p, icon: e.target.value }; patch("about", { pillars: n }); }} className="w-32" />
                  <button type="button" onClick={() => patch("about", { pillars: data.about.pillars.filter((_, j) => j !== i) })} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
                </div>
                <TextArea rows={2} placeholder="Description" value={p.description} onChange={(e) => { const n = [...data.about.pillars]; n[i] = { ...p, description: e.target.value }; patch("about", { pillars: n }); }} />
              </div>
            ))}
            <button type="button" onClick={() => patch("about", { pillars: [...data.about.pillars, { _uid: crypto.randomUUID(), title: "", description: "", icon: "Target" }] })} className="text-xs text-prepify-700 hover:underline">+ Add pillar</button>
          </div>
        </Field>
      </SectionShell>

      {/* TESTIMONIALS */}
      <SectionShell title="Testimonials" desc="Dark section showcasing client quotes." isVisible={data.testimonials.is_visible} onToggle={(v) => patch("testimonials", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.testimonials.eyebrow} onChange={(e) => patch("testimonials", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.testimonials.title} onChange={(e) => patch("testimonials", { title: e.target.value })} /></Field>
        <Field label="Testimonials">
          <CardList
            items={data.testimonials.items}
            emptyLabel="No testimonials yet"
            addLabel="Add testimonial"
            onAdd={() => patch("testimonials", { items: [...data.testimonials.items, { id: crypto.randomUUID(), quote: "", name: "", org: "", avatar_initial: "", rating: 5, order: data.testimonials.items.length, is_visible: true }] })}
            onChange={(items) => patch("testimonials", { items })}
            renderItem={(t, idx, up) => (
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3"><Field label="Quote"><TextArea rows={3} value={t.quote} onChange={(e) => up(idx, { quote: e.target.value })} /></Field></div>
                <Field label="Name"><Input value={t.name} onChange={(e) => up(idx, { name: e.target.value })} /></Field>
                <Field label="Org"><Input value={t.org} onChange={(e) => up(idx, { org: e.target.value })} /></Field>
                <Field label="Avatar Initial"><Input maxLength={2} value={t.avatar_initial} onChange={(e) => up(idx, { avatar_initial: e.target.value })} /></Field>
              </div>
            )}
          />
        </Field>
      </SectionShell>

      {/* PRICING */}
      <SectionShell title="Pricing" desc="Three-tier pricing plans (one can be featured)." isVisible={data.pricing.is_visible} onToggle={(v) => patch("pricing", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.pricing.eyebrow} onChange={(e) => patch("pricing", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.pricing.title} onChange={(e) => patch("pricing", { title: e.target.value })} /></Field>
        <Field label="Description"><TextArea rows={2} value={data.pricing.description} onChange={(e) => patch("pricing", { description: e.target.value })} /></Field>
        <Field label="Tiers">
          <CardList
            items={data.pricing.tiers}
            emptyLabel="No pricing tiers yet"
            addLabel="Add tier"
            onAdd={() => patch("pricing", { tiers: [...data.pricing.tiers, { id: crypto.randomUUID(), name: "New Tier", price: "", currency: "₹", period: "/month", description: "", features: [], cta_label: "Get Started", is_featured: false, featured_badge: "Most Popular", order: data.pricing.tiers.length, is_visible: true }] })}
            onChange={(tiers) => patch("pricing", { tiers })}
            renderItem={(tr, idx, up) => (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <Field label="Name"><Input value={tr.name} onChange={(e) => up(idx, { name: e.target.value })} /></Field>
                  <Field label="Currency"><Input value={tr.currency} onChange={(e) => up(idx, { currency: e.target.value })} /></Field>
                  <Field label="Price"><Input value={tr.price} onChange={(e) => up(idx, { price: e.target.value })} /></Field>
                  <Field label="Period"><Input value={tr.period} onChange={(e) => up(idx, { period: e.target.value })} /></Field>
                </div>
                <Field label="Description"><Input value={tr.description} onChange={(e) => up(idx, { description: e.target.value })} /></Field>
                <Field label="Features">
                  <SimpleListEditor items={tr.features} onChange={(v) => up(idx, { features: v })} placeholder="Feature line" />
                </Field>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <Field label="CTA Label"><Input value={tr.cta_label} onChange={(e) => up(idx, { cta_label: e.target.value })} /></Field>
                  <Field label="Featured Badge"><Input value={tr.featured_badge} onChange={(e) => up(idx, { featured_badge: e.target.value })} /></Field>
                  <label className="flex items-center gap-2 pb-2">
                    <input type="checkbox" checked={tr.is_featured} onChange={(e) => up(idx, { is_featured: e.target.checked })} />
                    <span className="text-sm">Featured</span>
                  </label>
                </div>
              </div>
            )}
          />
        </Field>
      </SectionShell>

      {/* CONTACT */}
      <SectionShell title="Contact Section" desc="Form that submits straight into your CRM Leads list." isVisible={data.contact.is_visible} onToggle={(v) => patch("contact", { is_visible: v })}>
        <Field label="Eyebrow"><Input value={data.contact.eyebrow} onChange={(e) => patch("contact", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><TextArea rows={2} value={data.contact.title} onChange={(e) => patch("contact", { title: e.target.value })} /></Field>
        <Field label="Description"><TextArea rows={2} value={data.contact.description} onChange={(e) => patch("contact", { description: e.target.value })} /></Field>
        <Field label="Reassurance bullets"><SimpleListEditor items={data.contact.bullets} onChange={(v) => patch("contact", { bullets: v })} placeholder="100% Confidential" /></Field>
        <Field label="Content type dropdown options"><SimpleListEditor items={data.contact.content_type_options} onChange={(v) => patch("contact", { content_type_options: v })} placeholder="Mock Tests" /></Field>
      </SectionShell>
    </div>
  );
};

export default HomepageEditor;

